// React error boundary. Without this, an unhandled throw in any component
// blanks the entire app — particularly bad for desktop, where there's no
// browser refresh button and the user has to relaunch the binary.
//
// Two boundaries to consider:
//   1. App-level (in App.tsx) — last line of defense.
//   2. Route-level (around <Routes>) — keeps shell + nav usable when
//      a single page crashes.
// We export the same component for both; pass a `scope` label to make
// the recovery copy more specific.

import { Component, type ErrorInfo, type ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { AlertTriangle } from "lucide-react";
import { captureError } from "@/lib/sentry";

interface Props {
  /** "app" | "route" | etc — only affects the copy + reset behaviour. */
  scope?: "app" | "route";
  children: ReactNode;
}

interface State {
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null, errorInfo: null };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Always log so the dev console sees it. We intentionally don't swallow
    // — the boundary just guarantees the app shell stays usable.
    console.error("[ErrorBoundary] caught:", error, errorInfo);
    void captureError(error, {
      scope: this.props.scope ?? "unknown",
      componentStack: errorInfo.componentStack,
    });
    this.setState({ errorInfo });
  }

  handleReset = (): void => {
    this.setState({ error: null, errorInfo: null });
  };

  handleReload = (): void => {
    window.location.reload();
  };

  render(): ReactNode {
    if (!this.state.error) return this.props.children;

    const isApp = this.props.scope === "app";

    return (
      <div className="min-h-[60vh] flex items-center justify-center p-6">
        <div className="max-w-lg w-full rounded-lg border bg-card p-6 shadow-sm space-y-4">
          <div className="flex items-center gap-3">
            <AlertTriangle className="h-6 w-6 text-destructive shrink-0" />
            <div>
              <h2 className="text-lg font-semibold">
                {isApp ? "Something broke" : "This page hit an error"}
              </h2>
              <p className="text-sm text-muted-foreground">
                {isApp
                  ? "The app crashed. Reload to try again."
                  : "Other parts of the app should still work. Try recovering, or reload if it stays broken."}
              </p>
            </div>
          </div>

          <details className="text-xs bg-muted rounded p-3 overflow-auto max-h-48">
            <summary className="cursor-pointer font-medium mb-2">
              Error details
            </summary>
            <pre className="whitespace-pre-wrap break-words mt-2 font-mono">
              {this.state.error.name}: {this.state.error.message}
              {"\n\n"}
              {this.state.error.stack ?? "(no stack)"}
            </pre>
          </details>

          <div className="flex gap-2">
            {!isApp && (
              <Button variant="outline" onClick={this.handleReset}>
                Try again
              </Button>
            )}
            <Button onClick={this.handleReload}>Reload app</Button>
          </div>
        </div>
      </div>
    );
  }
}
